import {
  type HighlightsController,
  type HighlightsRenderer
} from '@databases/highlights';
import { HIGHLIGHT_FOCUS_BOX_CLASS } from '@databases/highlights/constants';

import { type SelectionState } from '../../machine/state/types';
import { createEffect } from '../utils/create-effect';

import {
  FOCUS_LAYER_Z_INDEX,
  SELECTIONS_FOCUS_BOX_ELEMENT_ID,
  SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID
} from './constants';
import { isSingleCellSelection } from './utils/is-single-cell-selection';

/**
 * Creates the effect that draws the focus box (the main cell border)
 * on the currently focused cell when selection is exactly one cell in cell mode.
 *
 * Syncs from post-dispatch `SelectionState` on focus and selection deltas so the
 * ring hides when the range grows or non-cell modes apply, even without a
 * `focusChange` field on the delta.
 */
export const createFocusBoxEffect = createEffect((controller: HighlightsController) => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------
  let scrollableContainer: HTMLElement | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------
  let focusRenderer: HighlightsRenderer | null = null;

  function ensureDependentElementsAndRenderer() {
    if (!scrollableContainer) {
      scrollableContainer = controller.getScrollContainer();
    }

    if (!focusRenderer && scrollableContainer) {
      focusRenderer = controller.createLayer();
      focusRenderer.add({
        range: {
          id: SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID,
          anchor: { row: 0, col: 0 },
          lead: { row: 0, col: 0 }
        },
        mode: 'none',
        zIndex: FOCUS_LAYER_Z_INDEX,
        className: HIGHLIGHT_FOCUS_BOX_CLASS,
        elementId: SELECTIONS_FOCUS_BOX_ELEMENT_ID
      });
    }

    return !!(focusRenderer && scrollableContainer);
  }

  function syncFocusBoxFromState(state: SelectionState) {
    if (!focusRenderer) {
      return;
    }

    const eligible =
      state.mode === 'cell' &&
      state.selections.length === 1 &&
      isSingleCellSelection(state.selections[0], state.mode);

    const selectedCell = eligible ? (state.selections[0]?.lead ?? null) : null;
    const focus = selectedCell ?? state.focusedCell;

    const highlightMode = selectedCell ? 'cell' : 'none';
    const row = focus?.row.index ?? 0;
    const col = focus?.col.index ?? 0;

    focusRenderer.update(
      {
        id: SELECTIONS_FOCUS_BOX_HIGHLIGHT_ID,
        anchor: { row, col },
        lead: { row, col }
      },
      highlightMode
    );
  }

  return {
    deltaTypes: [
      'focusChanged',
      'selectionModified',
      'selectionReplaced',
      'selectionPreview',
      'cellEditRequested',
      'cellEditStopped'
    ],

    dispose() {
      if (focusRenderer) {
        focusRenderer.dispose();
        controller.removeLayer(focusRenderer);
        focusRenderer = null;
      }

      scrollableContainer = null;
    },

    onDelta(delta, state) {
      if (
        !ensureDependentElementsAndRenderer() ||
        !focusRenderer ||
        !scrollableContainer
      ) {
        return;
      }

      if (delta.kind === 'cellEditRequested') {
        focusRenderer.hideAll();

        return;
      }

      if (delta.kind === 'cellEditStopped') {
        focusRenderer.showAll();
        syncFocusBoxFromState(state);

        return;
      }

      syncFocusBoxFromState(state);
    }
  };
});
